import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { Close } from '@material-symbols-svg/react/rounded/w500';
import { useMemo } from 'react';

import { pendingNetHours } from '../calendar';
import { formatHM, formatLongDay } from '../common';
import { ModalBackdrop } from '../components';
import { palette, tokens, useAppButtonStyles } from '../theme';
import { workItemTypeAccent } from '../work-items';
import { groupPendingByDate, useApplyPending, usePending } from '../worklog';

export type ApplyDialogProps = {
    onClose: () => void;
};

export const ApplyDialog: React.FC<ApplyDialogProps> = ({ onClose }) => {
    const classes = useStyles();
    const buttons = useAppButtonStyles();
    const { pending } = usePending();
    const { apply, applying, error } = useApplyPending();

    const totalHours = useMemo(() => pendingNetHours(pending), [pending]);
    const entryCount = pending.length;

    const groups = useMemo(() => groupPendingByDate(pending), [pending]);

    const handleApply = async () => {
        if (await apply()) {
            onClose();
        }
    };

    return (
        <ModalBackdrop
            onClose={onClose}
            ariaLabel={`Apply ${entryCount} pending ${entryCount === 1 ? 'entry' : 'entries'}`}
            align="right"
            closeOnBackdropClick={!applying}
            closeOnEscape={!applying}
        >
            <aside className={classes.panel}>
                <header className={classes.header}>
                    <div className={classes.who}>
                        <h2 className={classes.title}>
                            Apply {entryCount} pending {entryCount === 1 ? 'entry' : 'entries'}
                        </h2>
                        <p className={classes.subtitle}>
                            Posts {formatHM(totalHours)} h total to Azure DevOps — one PATCH on CompletedWork plus a [time:YYYY-MM-DD]
                            comment per entry.
                        </p>
                    </div>
                    <button type="button" className={classes.closeBtn} aria-label="Close" onClick={onClose} disabled={applying}>
                        <Close className={classes.closeIcon} />
                    </button>
                </header>

                <div className={classes.body}>
                    <section className={classes.section}>
                        <h3 className={classes.sectionTitle}>Pending entries</h3>

                        <ol className={classes.list}>
                            {groups.map(([date, entries]) => (
                                <li key={date} className={classes.dayGroup}>
                                    <div className={classes.dayHeader}>{formatLongDay(date)}</div>
                                    {entries.map((entry) => (
                                        <div key={entry.id} className={classes.row}>
                                            <span
                                                className={classes.typeDot}
                                                style={{
                                                    backgroundColor: workItemTypeAccent(entry.workItemType, entry.workItemTitle),
                                                }}
                                                aria-label={entry.workItemType}
                                            />
                                            <span className={classes.idText}>#{entry.workItemId}</span>
                                            <span className={classes.titleText}>{entry.workItemTitle}</span>
                                            <span
                                                className={classes.hoursText}
                                                style={{
                                                    color: entry.action === 'remove' ? palette.overText : palette.loggedAccent,
                                                }}
                                            >
                                                {entry.action === 'remove' ? '-' : '+'}
                                                {formatHM(entry.hours)}
                                            </span>
                                        </div>
                                    ))}
                                </li>
                            ))}
                        </ol>
                    </section>

                    {error && <p className={classes.error}>{error}</p>}
                </div>

                <footer className={classes.footer}>
                    <button
                        type="button"
                        className={mergeClasses(buttons.secondary, classes.footerBtn)}
                        disabled={applying}
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={mergeClasses(buttons.primary, classes.footerBtn)}
                        disabled={applying || entryCount === 0}
                        onClick={handleApply}
                    >
                        {applying ? 'Applying…' : 'Confirm'}
                    </button>
                </footer>
            </aside>
        </ModalBackdrop>
    );
};

const useStyles = makeStyles({
    panel: {
        width: 'clamp(360px, 30vw, 96vw)',
        height: '100%',
        backgroundColor: palette.panelBg,
        boxShadow: palette.sidePanelShadow,
        display: 'grid',
        gridTemplateRows: 'auto minmax(0, 1fr) auto',
    },
    header: {
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'flex-start',
        columnGap: tokens.spacingHorizontalM,
        padding: tokens.spacingHorizontalL,
        paddingBottom: tokens.spacingVerticalS,
    },
    who: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        minWidth: 0,
    },
    title: {
        margin: 0,
        fontSize: '17px',
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground1,
    },
    subtitle: {
        margin: 0,
        fontSize: '12.5px',
        color: tokens.colorNeutralForeground3,
        lineHeight: 1.4,
    },
    closeBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        border: 'none',
        background: 'transparent',
        color: tokens.colorNeutralForeground2,
        borderRadius: tokens.borderRadiusMedium,
        cursor: 'pointer',
        '&:hover:not(:disabled)': {
            backgroundColor: palette.panelMuted,
        },
        '&:disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
        },
    },
    closeIcon: {
        width: '20px',
        height: '20px',
    },
    body: {
        overflowY: 'auto',
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    sectionTitle: {
        margin: 0,
        fontSize: '13px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
    },
    list: {
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    dayGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    dayHeader: {
        fontSize: '11px',
        fontWeight: tokens.fontWeightSemibold,
        color: palette.dimText,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        padding: '6px 4px',
        borderBottom: `1px solid ${palette.cardBorder}`,
        marginBottom: '4px',
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '8px 56px minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        padding: '8px 4px',
        fontSize: '13px',
    },
    typeDot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    idText: {
        fontSize: '12px',
        color: tokens.colorNeutralForeground3,
    },
    titleText: {
        color: tokens.colorNeutralForeground1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    hoursText: {
        fontSize: '13px',
        fontWeight: tokens.fontWeightSemibold,
        textAlign: 'right',
        minWidth: '48px',
    },
    error: {
        margin: 0,
        fontSize: '12.5px',
        color: palette.overText,
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: tokens.spacingHorizontalS,
        padding: tokens.spacingHorizontalL,
        borderTop: `1px solid ${palette.cardBorder}`,
        backgroundColor: palette.panelBg,
    },
    footerBtn: {
        minWidth: '110px',
    },
});
